import type { Sort } from "@/features/status/components/sort-button";

type Params = {
  [key: string]: any;
};

const StatusQueryKeys = {
  global: ["statuses"] as const,

  /**
   * Status Details
   */

  /** @deprecated Internal: do not use */
  details: () => [...StatusQueryKeys.global, "details"] as const,
  /** Used to fetching a single Status */
  detail: (statusId: string) =>
    [...StatusQueryKeys.details(), statusId] as const,
  /** Used for fetching ancestors on a single Status */
  ancestors: (statusId: string) =>
    [...StatusQueryKeys.detail(statusId), "ancestors"] as const,
  /** Used for fetching descendants on a single Status */
  descendants: (statusId: string, sort: Sort) =>
    [...StatusQueryKeys.detail(statusId), "descendants", sort] as const,
  /** Used for fetching favourites on a single Status */
  favourites: (id: string) =>
    [...StatusQueryKeys.detail(id), "favourites"] as const,
  /** Used for fetching edit history on a single Status */
  history: (statusId: string) =>
    [...StatusQueryKeys.detail(statusId), "history"] as const,
  /** Used for fetching reactions to Group statuses (upvote/downvote) */
  reactions: (statusId: string) =>
    [...StatusQueryKeys.detail(statusId), "reactions"] as const,

  /**
   * Home Timeline
   */
  timeline: (timelineId: string, pinKey: string | null) =>
    [...StatusQueryKeys.global, "timeline", timelineId, pinKey] as const,

  /**
   * Account Timeline (Profile)
   */

  /** @deprecated Internal: do not use */
  accounts: () => [...StatusQueryKeys.global, "account"] as const,
  /** Used to bust the cache of a particular account's statuses */
  accountId: (accountId: string, params: Params = {}) =>
    [...StatusQueryKeys.accounts(), accountId] as const,
  /** Used for fetching Statuses from a single Account */
  account: (accountId: string, params: Params = {}) =>
    [...StatusQueryKeys.accounts(), accountId, { ...params }] as const,
  /** Used for fetching Favourited Statuses from my Account */
  accountFavourites: (accountId: string) =>
    [...StatusQueryKeys.account(accountId), "favourites"] as const,

  /**
   * Group Timeline
   */
  groupTimeline: (groupId: string, pinned: boolean) =>
    [
      ...StatusQueryKeys.global,
      "group",
      groupId,
      "timeline",
      "pinned",
      String(pinned),
    ] as const,
  groupTagTimeline: (groupId: string, tagName: string) =>
    [...StatusQueryKeys.global, "group", groupId, "tag", tagName] as const,
  groupMedia: (groupId?: string) =>
    [...StatusQueryKeys.global, "group", groupId, "media"] as const,
  trendingFromGroups: () =>
    [...StatusQueryKeys.global, "groups", "trending"] as const,

  /**
   * Tag Timeline
   */
  tagTimeline: (tagName?: string) =>
    [...StatusQueryKeys.global, "tag", tagName] as const,

  /**
   * Prediction Timeline
   */
  predictionTimeline: (predictionId: string) =>
    [
      ...StatusQueryKeys.global,
      "prediction",
      predictionId,
      "timeline",
    ] as const,

  /**
   *  Sports Game Conversation Timeline
   */
  gameConversation: (sportType: string, gameId: string) =>
    [
      ...StatusQueryKeys.global,
      "game",
      sportType,
      gameId,
      "conversation",
    ] as const,

  /**
   *  Sports Team Conversation Timeline
   */
  teamConversation: (sportType: string, teamId: string) =>
    [
      ...StatusQueryKeys.global,
      "team",
      sportType,
      teamId,
      "conversation",
    ] as const,

  /**
   * Miscellaneous
   */

  mutes: () => [...StatusQueryKeys.global, "mutes"] as const,
  trending: () => [...StatusQueryKeys.global, "trending"] as const,
  bookmarks: () => [...StatusQueryKeys.global, "bookmarks"] as const,
  landing: () => [...StatusQueryKeys.global, "landing"] as const,
  videos: (initialStatusId: string) =>
    [...StatusQueryKeys.global, "videos", initialStatusId] as const,
};

export { StatusQueryKeys };
